import {
  Label,
  LabelType,
  LabelValue,
  LabelValueAcceptedBy,
  LabelValueSource
} from 'graphql/types';
import { TimesheetLabel } from '../../labelSchema';

export const filterLabelsByType = <T extends Label>(
  labels: T[],
  labelType: LabelType
): T[] => {
  return labels.filter((label: Label) => label.type === labelType);
};

export const isLabelType = <T extends Label>(
  label: T,
  labelType: LabelType
): boolean => label.type === labelType;

export const mapDefaultValue = (
  label: Label,
  defaultValue: undefined | null
): LabelValue | undefined | null => {
  if (!label || !label.defaultValue) return defaultValue;
  return {
    idValue: label.defaultValue?.idValue,
    stringValue: label.defaultValue?.stringValue,
    numericValue: label.defaultValue?.numericValue || null
  };
};

export const determineLabelAcceptedBy = (
  valueSource: LabelValueSource,
  acceptedBy: LabelValueAcceptedBy | null | undefined,
  isSmartSuggestionEnabled: boolean
): LabelValueAcceptedBy | null => {
  if (
    valueSource === LabelValueSource.User ||
    // Should we enforce accepted by user when suggestion off? If the user turns the setting off then we end up rewriting history for the label.
    !isSmartSuggestionEnabled
  ) {
    return LabelValueAcceptedBy.User;
  }
  return acceptedBy ?? null;
};

export const isSuggestion = (
  isSmartSuggestionEnabled: boolean,
  label: TimesheetLabel | undefined | null
): boolean => {
  return (
    isSmartSuggestionEnabled &&
    label?.valueSource === LabelValueSource.Suggestion &&
    !label?.acceptedBy
  );
};

export const isLabelValueEmpty = (
  value: LabelValue | null | undefined
): boolean => {
  return !value?.idValue && !value?.stringValue && !value?.numericValue;
};

export const shouldAcceptSuggestion = (
  label: TimesheetLabel,
  isEmptyLabelsEnabled: boolean
) => {
  return (
    isLabelValueEmpty(label.value) &&
    (isEmptyLabelsEnabled ? !label.acceptedBy : true)
  );
};

export enum LabelState {
  SELECTED = 'selected',
  SUGGESTED = 'suggested',
  OMITTED = 'omitted',
  NOT_SELECTED = 'notSelected'
}

export const getLabelState = (
  label: TimesheetLabel | null | undefined,
  isSmartSuggestionEnabled: boolean
): LabelState => {
  if (!label) {
    return LabelState.NOT_SELECTED;
  }

  if (!isLabelValueEmpty(label.value) && label.acceptedBy) {
    return LabelState.SELECTED;
  }

  if (
    isSmartSuggestionEnabled &&
    isLabelValueEmpty(label.value) &&
    label.acceptedBy
  ) {
    return LabelState.OMITTED;
  }

  if (
    isSmartSuggestionEnabled &&
    isLabelValueEmpty(label.value) &&
    label?.suggestedValues &&
    label?.suggestedValues?.length > 0 &&
    !label.acceptedBy &&
    label.valueSource === LabelValueSource.Suggestion
  ) {
    return LabelState.SUGGESTED;
  }

  return LabelState.NOT_SELECTED;
};
